import { render, h } from 'preact';
import { App } from './App';
import { SDKContext } from './context';
import { AdpxStore } from './store';
import { addFontImport, prefetchImages } from './utils';
import { PLATFORM_MESSAGES, DEFAULT_API_URL, DEFAULT_CDN_URL, FINGERPRINT_KEY, ADPX_API_RESPONSE_KEY, USP_SELECTED_OFFERS_KEY, } from './constants';
import { createDebugLogger, enableDebugLogging, } from './utils/debug';
import { resolveBundledCustomLayout } from './customLayoutPresets/resolveBundledCustomLayout';
import { COU_PROGRESS_DEFAULT_CSS } from './utils/couProgress';
import { getSOUStylesheet } from './styles/SOUStyles';
import mouShadowStyles from './styles/MOU.css';
let instanceCounter = 0;
// Shared across all instances so fpAgent.get() runs only once per page load.
let fingerprintPromise = null;
/**
 * MomentScience SDK — a single instance renders one offer flow
 * (SOU / MOU / embedded) into its own isolated shadow root. Multiple instances
 * can run concurrently on the same page; each has its own store, shadow root,
 * DOM ids, payload, and user callback.
 */
export class MomentScienceSDK {
    constructor(name) {
        /** Per-instance USP (User Selected Perks) state. Tracks which offer IDs the user has checked. */
        this.mouState = { selectedOffers: [] };
        /** True when USP selections were restored from a prefetch cache — suppresses auto-select-all on mount. */
        this.reused_session = false;
        this._uspSelectTimers = new Map();
        this._uspUnselectTimers = new Map();
        /**
         * Set when `setApiResponse` wants an `autoShow` display but the instance is
         * not ready yet (the in-init `fetchAds`, which runs before the Preact app is
         * rendered and before `isInitialized`). `init()` consumes it once ready, so
         * the display happens exactly once instead of being refused by `show()`.
         */
        this.pendingInitialAutoShow = false;
        this.shadowContainer = null;
        this.shadowRoot = null;
        this.embedTargetObserver = null;
        instanceCounter += 1;
        this.instanceName = name ?? `ms-${instanceCounter}`;
        this.debug = createDebugLogger(this.instanceName);
        this.instanceId = `ms-${instanceCounter}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;
        this.store = new AdpxStore();
        this.isInitialized = false;
        this.apiUrl = DEFAULT_API_URL;
        this.cdnUrl = DEFAULT_CDN_URL;
        this.prefetch = false;
        this.autoLoad = true;
        this.autoShow = true;
        this.isDev = false;
        this.isEmbedded = false;
        this.isWebView = false;
        this.userAttributes = {};
        this.apiPayload = {};
        this.currentAdIndex = 0;
        this.ready = new Promise((resolve, reject) => {
            this.readyResolve = resolve;
            this.readyReject = reject;
        });
        this.init = this.init.bind(this);
        this.show = this.show.bind(this);
        this.reload = this.reload.bind(this);
        this.refresh = this.refresh.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.fetchAds = this.fetchAds.bind(this);
        this.setApiResponse = this.setApiResponse.bind(this);
        this.getPayload = this.getPayload.bind(this);
        this.setPayload = this.setPayload.bind(this);
        this.onLinkClicked = this.onLinkClicked.bind(this);
        this.loadAndSetFingerprint = this.loadAndSetFingerprint.bind(this);
        this.debug.log('constructor called');
    }
    getStore() {
        return this.store;
    }
    get settings() {
        return (this.store.getSettings() ?? MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS);
    }
    get publisherStyles() {
        return this.store.getStyles() ?? {};
    }
    get adsOnDisplay() {
        return this.store.getOffers();
    }
    get totalAdsOnDisplay() {
        return this.store.getTotalOffers();
    }
    get currentSessionId() {
        return this.store.getSessionId();
    }
    get currentFingerprint() {
        return this.store.getFingerprint();
    }
    async init(options) {
        if (this.isInitialized) {
            this.debug.warn('Already initialized.');
            return;
        }
        if (!options.accountId && !options.awinAdvertiserId) {
            const err = 'Authentication failed: accountId or awinAdvertiserId required';
            this.debug.error(err);
            // The prefix is spelled out here, not by the logger: this string is the
            // rejected Error's message, which publishers see out of band.
            this.readyReject(new Error(`[MS:${this.instanceName}] ${err}`));
            return;
        }
        try {
            // Clear any flag left behind by a failed init or a destroy() / init() cycle.
            // This must happen before init() can yield: `MS.init()` returns the instance
            // while this method is still awaiting, so a publisher calling `setApiResponse`
            // right after it would otherwise have its deferred display wiped here.
            this.pendingInitialAutoShow = false;
            this.accountId = options.accountId;
            this.awinAdvertiserId = options.awinAdvertiserId ?? '';
            this.campaignId = options.campaignId ?? '';
            this.theme_id = options.theme_id ?? '';
            this.offerId = options.offerId ?? 0;
            this.subId = options.subId === undefined ? null : options.subId;
            this.prefetch = options.prefetch ?? this.prefetch;
            this.autoLoad = options.autoLoad ?? this.autoLoad;
            this.autoShow = options.autoShow ?? this.autoShow;
            this.dev = options.dev ?? false;
            // One-way switch: `dev: true` turns logging on for the page, `dev: false`
            // never turns it off, so a publisher config can't defeat the `ms_debug`
            // hatch or a staging build. `isDev` is deliberately NOT wired in here —
            // it is a separate, unrelated option that nothing reads.
            if (this.dev) {
                enableDebugLogging();
            }
            this.isDev = options.isDev ?? false;
            this.isEmbedded = options.settings?.embedded?.enabled ?? false;
            this.isWebView = options.isWebView ?? false;
            this.userCallback = options.userCallback ?? (() => { });
            this.userAttributes = {
                ...(options.user ?? {}),
                ...(options.attributes ?? {}),
            };
            if (options.subId === undefined && this.userAttributes.subid) {
                this.subId = this.userAttributes.subid;
            }
            this.subId =
                this.subId !== null ? encodeURIComponent(this.subId ?? '') : null;
            const initialSettings = {
                ...MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS,
                ...(options.settings ?? {}),
                embedded: {
                    ...MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS.embedded,
                    ...(options.settings?.embedded ?? {}),
                },
            };
            if (this.isEmbedded) {
                initialSettings.embedded = {
                    ...initialSettings.embedded,
                    enabled: true,
                };
            }
            this.store.setSettings(initialSettings);
            this.store.setStyles({ ...(options.styles ?? {}) });
            if (options.customLayout !== undefined) {
                this.customLayout = options.customLayout;
            }
            this.appTargetSelector = options.targetSelector ?? '';
            this.appApiKey = options.apiKey ?? '';
            this.debug.log('initializing with resolved config:', () => ({
                ...options,
                resolvedSettings: this.settings,
            }));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructure to exclude user/attributes from the api payload rest spread; both are already merged into userAttributes above
            const { user: _u, attributes: _a, ...configOptions } = options;
            this.apiPayload = { ...configOptions, ...this.userAttributes };
            await this.loadAndSetFingerprint(options.fingerprint);
            if (this.autoLoad) {
                await this.fetchAds();
            }
            const shadowRoot = this.getShadowRoot();
            if (shadowRoot) {
                const appProps = this.appApiKey ? { apiKey: this.appApiKey } : {};
                render(h(SDKContext.Provider, { value: { sdk: this, store: this.store } }, h(App, appProps)), shadowRoot);
            }
            this.isInitialized = true;
            if (!this.appTargetSelector) {
                this.debug.log('core initialized.');
            }
            if (this.pendingInitialAutoShow) {
                this.pendingInitialAutoShow = false;
                this.show();
            }
            this.readyResolve();
        }
        catch (err) {
            this.debug.error('init failed:', err);
            this.readyReject(err);
            throw err;
        }
    }
    async loadAndSetFingerprint(initialFingerprint) {
        this.debug.log('loading fingerprint...');
        if (initialFingerprint) {
            this.store.setFingerprint(initialFingerprint);
        }
        else if (this.userAttributes.adpx_fp) {
            this.store.setFingerprint(this.userAttributes.adpx_fp);
        }
        else {
            try {
                const fpFromStorage = localStorage.getItem(FINGERPRINT_KEY);
                if (fpFromStorage) {
                    try {
                        const storedFpData = JSON.parse(fpFromStorage);
                        this.store.setFingerprint(storedFpData.fingerprint);
                    }
                    catch (e) {
                        this.debug.warn('Could not parse fingerprint from localStorage.', e);
                        localStorage.removeItem(FINGERPRINT_KEY);
                    }
                }
            }
            catch (error) {
                this.debug.error('Error accessing localStorage for fingerprint:', error);
            }
        }
        if (!this.currentFingerprint) {
            let fpJsModule = window.FingerprintJS;
            if (!fpJsModule) {
                try {
                    const importedModule = await import(
                    /* @vite-ignore */ `${this.cdnUrl}/fingerprint.v3.js`);
                    fpJsModule = importedModule;
                }
                catch (_e) {
                    this.debug.error('Failed to load FingerprintJS:', _e);
                    fpJsModule = null;
                }
            }
            if (fpJsModule && typeof fpJsModule.load === 'function') {
                if (!fingerprintPromise) {
                    fingerprintPromise = fpJsModule.load()
                        .then(fpAgent => fpAgent.get())
                        .then(result => result.visitorId)
                        .catch((error) => {
                        // Clear so the next MS.init() can retry fingerprinting.
                        fingerprintPromise = null;
                        return Promise.reject(error);
                    });
                }
                try {
                    const generatedFingerprint = await fingerprintPromise;
                    this.store.setFingerprint(generatedFingerprint);
                    localStorage.setItem(FINGERPRINT_KEY, JSON.stringify({
                        fingerprint: generatedFingerprint,
                        timestamp: Date.now(),
                    }));
                }
                catch (error) {
                    this.debug.error('Error during fingerprint generation:', error);
                }
            }
            else if (!this.currentFingerprint) {
                this.debug.warn('FingerprintJS not available; skipping generation.');
            }
        }
        if (this.currentFingerprint) {
            this.apiPayload.adpx_fp = this.currentFingerprint;
        }
        else {
            this.debug.warn('Proceeding without fingerprint (adpx_fp).');
        }
    }
    /** Per-instance localStorage key so multiple instances don't share cached responses. */
    getApiCacheKey() {
        const id = this.accountId || this.awinAdvertiserId || 'default';
        return `${ADPX_API_RESPONSE_KEY}__${id}`;
    }
    async fetchAds() {
        if (!this.apiPayload.adpx_fp && !this.currentFingerprint) {
            this.debug.warn('Fingerprint not available yet, delaying fetchAds slightly.');
            await new Promise(resolve => setTimeout(resolve, 500));
            if (!this.currentFingerprint) {
                this.debug.error('Fingerprint still unavailable. Cannot fetch ads.');
                return;
            }
            this.apiPayload.adpx_fp = this.currentFingerprint;
        }
        const cacheKey = this.getApiCacheKey();
        let apiResponseToProcess = null;
        this.reused_session = false;
        this.mouState = { selectedOffers: [] };
        this._uspSelectTimers.forEach(t => clearTimeout(t));
        this._uspSelectTimers.clear();
        this._uspUnselectTimers.forEach(t => clearTimeout(t));
        this._uspUnselectTimers.clear();
        if (this.prefetch) {
            const cachedResponseStr = localStorage.getItem(cacheKey);
            if (cachedResponseStr) {
                try {
                    const cachedResponse = JSON.parse(cachedResponseStr);
                    const isTimestampValid = cachedResponse.timestamp &&
                        Date.now() - cachedResponse.timestamp < 1 * 60 * 60 * 1000;
                    if (isTimestampValid) {
                        localStorage.removeItem(cacheKey);
                        apiResponseToProcess = cachedResponse;
                        // Restore USP selections from a prior session so OfferGrid can
                        // pre-check them without calling auto-select-all again.
                        this.reused_session = this.restoreUspSession();
                    }
                    else {
                        localStorage.removeItem(cacheKey);
                    }
                }
                catch (e) {
                    this.debug.warn('Could not parse cached API response.', e);
                    localStorage.removeItem(cacheKey);
                }
            }
        }
        if (!apiResponseToProcess) {
            this.debug.log('Fetching ads. Payload:', () => JSON.stringify(this.apiPayload, null, 2));
            try {
                const response = await fetch(`${this.apiUrl}/sdk/v2/offers.json`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.apiPayload),
                });
                if (!response.ok) {
                    this.debug.error(`API request failed with status ${response.status}`);
                    this.sendMessage(PLATFORM_MESSAGES.NO_ADS.name, PLATFORM_MESSAGES.NO_ADS);
                    return;
                }
                apiResponseToProcess = (await response.json());
                if (this.prefetch && apiResponseToProcess) {
                    localStorage.setItem(cacheKey, JSON.stringify({ ...apiResponseToProcess, timestamp: Date.now() }));
                }
            }
            catch (error) {
                this.debug.error('Error fetching ads:', error);
                this.sendMessage(PLATFORM_MESSAGES.NO_ADS.name, PLATFORM_MESSAGES.NO_ADS);
                return;
            }
        }
        if (apiResponseToProcess) {
            this.setApiResponse(apiResponseToProcess);
        }
        else {
            this.debug.warn('No API response to process after fetchAds.');
        }
    }
    setApiResponse(response) {
        if (!response || !response.data) {
            this.debug.error('Invalid API response received.');
            this.store.setError('Invalid API response received');
            this.sendMessage(PLATFORM_MESSAGES.NO_ADS.name, PLATFORM_MESSAGES.NO_ADS);
            delete this.lastResponseTemplate;
            return;
        }
        const body = response.data;
        const apiStyles = body.styles;
        const couTemplate = apiStyles?.custom_unit_template;
        const templateHtml = couTemplate?.html ?? (typeof body.template === 'string' ? body.template : undefined);
        if (typeof templateHtml === 'string') {
            this.lastResponseTemplate = templateHtml;
        }
        else {
            delete this.lastResponseTemplate;
        }
        const couCss = typeof couTemplate?.css === 'string' ? couTemplate.css.trim() : '';
        if (couCss) {
            this.lastResponseCss = couCss;
        }
        else {
            delete this.lastResponseCss;
        }
        this.store.setSessionId(body.session_id);
        this.apiPayload.session_id = body.session_id;
        const apiProvidedSettings = body.styles?.settings ?? {};
        const mergedSettings = {
            ...MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS,
            ...apiProvidedSettings,
            ...(this.apiPayload.settings ?? {}),
            embedded: {
                ...MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS.embedded,
                ...(this.apiPayload.settings?.embedded ?? {}),
                ...(apiProvidedSettings.embedded ?? {}),
            },
        };
        if (this.isEmbedded) {
            mergedSettings.embedded = { ...mergedSettings.embedded, enabled: true };
        }
        this.store.setSettings(mergedSettings);
        const apiProvidedStyles = body.styles ?? {};
        const mergedStyles = {
            header: {
                ...(apiProvidedStyles.header ?? {}),
                ...(this.apiPayload.styles?.header ?? {}),
            },
            footer: {
                ...(apiProvidedStyles.footer ?? {}),
                ...(this.apiPayload.styles?.footer ?? {}),
            },
            offerText: {
                ...(apiProvidedStyles.offerText ?? {}),
                ...(this.apiPayload.styles?.offerText ?? {}),
            },
            popup: {
                ...(apiProvidedStyles.popup ?? {}),
                ...(this.apiPayload.styles?.popup ?? {}),
            },
            offerwall: {
                ...(apiProvidedStyles.offerwall ?? {}),
                ...(this.apiPayload.styles?.offerwall ?? {}),
            },
        };
        this.store.setStyles(mergedStyles);
        if (mergedStyles.offerText?.font) {
            addFontImport(mergedStyles.offerText.font ?? 'Roboto');
        }
        const currentSettings = this.store.getSettings();
        if (currentSettings) {
            this.store.updateSettings({
                multi_offer_unit: currentSettings.multi_offer_unit ?? false,
            });
        }
        const offers = body.offers ?? [];
        const processedOffers = [];
        /**
         * Only `offer.image` is prefetched. It is the one creative every unit type
         * renders straight from the offer. `campaign.campaign_images[]` is not
         * prefetched: SOU and MOU never read it, and a COU reaches it only through
         * the `hero_image` / `icon_image` / `offer_image` macros, whose resolved
         * `src` routinely carries template query params (`?width=500`) that make it
         * a different URL from the raw one here, so warming the raw URL warms
         * nothing while still competing for the connection.
         */
        const imagesToPrefetch = new Set();
        for (const offer of offers) {
            const processedOffer = { ...offer };
            if (processedOffer.external_id === '')
                processedOffer.external_id = 0;
            if (processedOffer.image) {
                imagesToPrefetch.add(processedOffer.image);
            }
            processedOffers.push(processedOffer);
        }
        this.store.setOffers(processedOffers);
        this._resetState();
        if (imagesToPrefetch.size > 0) {
            prefetchImages([...imagesToPrefetch]);
        }
        const totalOffers = this.store.getTotalOffers();
        this.debug.log(() => `Processed ${totalOffers} ads.`);
        const messageData = {
            total: totalOffers,
            settings: this.store.getSettings(),
            ...(this.prefetch && totalOffers > 0 ? { response } : {}),
        };
        const messageName = totalOffers > 0
            ? PLATFORM_MESSAGES.ADS_FOUND.name
            : PLATFORM_MESSAGES.NO_ADS.name;
        const finalMessage = totalOffers > 0
            ? { ...PLATFORM_MESSAGES.ADS_FOUND, ...messageData }
            : PLATFORM_MESSAGES.NO_ADS;
        this.sendMessage(messageName, finalMessage);
        if (this.autoShow && totalOffers > 0) {
            if (this.isInitialized) {
                this.show();
            }
            else {
                this.pendingInitialAutoShow = true;
            }
        }
        else if (this.autoShow && totalOffers === 0) {
            this.debug.log('AutoShow true, but no ads.');
        }
    }
    show() {
        const totalOffers = this.store.getTotalOffers();
        if (!this.isInitialized || totalOffers === 0) {
            this.debug.warn('Cannot show. Not initialized or no ads.');
            return;
        }
        this.store.requestShow();
    }
    /** True when this instance should mount / style as embedded (init or merged store). */
    embeddedHostActive() {
        return (this.isEmbedded === true ||
            this.store.getSettings()?.embedded?.enabled === true);
    }
    /**
     * DOM id for the mount slot. Publisher `MS.init({ settings.embedded })` wins over
     * API-merged store values so `setApiResponse` cannot replace e.g. `shop-offer-slot`
     * with the network default `adpx`.
     */
    embeddedTargetElementId() {
        const fallback = MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS.embedded?.targetElement ??
            'adpx';
        const initEl = this.apiPayload?.settings?.embedded?.targetElement;
        if (typeof initEl === 'string' && initEl.trim()) {
            return initEl.trim();
        }
        const storeEl = this.store.getSettings()?.embedded?.targetElement;
        if (typeof storeEl === 'string' && storeEl.trim()) {
            return storeEl.trim();
        }
        return String(fallback);
    }
    /**
     * Modal hosts attach to `document.body`. Embedded hosts attach to the
     * element whose id matches `embeddedTargetElementId()` (init `targetElement`,
     * else store, else default `adpx`). Returns null when the target element
     * is not yet in the DOM — caller should defer via watchForEmbedTarget.
     */
    resolveShadowHostParent() {
        if (!this.embeddedHostActive()) {
            return document.body;
        }
        const id = this.embeddedTargetElementId();
        const target = document.getElementById(id);
        if (target) {
            return target;
        }
        this.debug.warn(`Embedded mode: target element #${id} not found — waiting for it to appear.`);
        return null;
    }
    watchForEmbedTarget() {
        if (this.embedTargetObserver)
            return;
        const id = this.embeddedTargetElementId();
        this.embedTargetObserver = new MutationObserver(() => {
            if (!document.getElementById(id))
                return;
            this.embedTargetObserver?.disconnect();
            this.embedTargetObserver = null;
            this.renderApp();
        });
        this.embedTargetObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
    /** Light-DOM styles on the shadow host node (outside the shadow tree). */
    shadowHostLightDomStyles() {
        if (!this.embeddedHostActive()) {
            return ('position: fixed; top: 0; left: 0; width: 100%; height: 100%; ' +
                'pointer-events: none; z-index: 2147483647;');
        }
        const fill = this.apiPayload?.settings?.embedded?.fill_container === true ||
            this.store.getSettings()?.embedded?.fill_container === true;
        if (fill) {
            return ('display: block; width: 100%; min-height: 100%; height: 100%; ' +
                'box-sizing: border-box;');
        }
        return 'display: block; width: 100%; min-height: 0; box-sizing: border-box;';
    }
    getShadowRoot() {
        if (this.shadowRoot)
            return this.shadowRoot;
        const parent = this.resolveShadowHostParent();
        if (!parent) {
            this.watchForEmbedTarget();
            return null;
        }
        this.shadowContainer = document.createElement('div');
        // Namespace per instance so two SDKs can coexist on the same page.
        this.shadowContainer.id = `ms-shadow-host-${this.instanceId}`;
        this.shadowContainer.setAttribute('data-ms-instance', this.instanceName);
        this.shadowContainer.style.cssText = this.shadowHostLightDomStyles();
        this.shadowRoot = this.shadowContainer.attachShadow({ mode: 'open' });
        this.store.setShadowRoot(this.shadowRoot);
        this.injectStylesIntoShadow();
        parent.appendChild(this.shadowContainer);
        return this.shadowRoot;
    }
    injectStylesIntoShadow() {
        if (!this.shadowRoot)
            return;
        const settings = this.store.getSettings();
        const embedded = this.embeddedHostActive();
        const fillHost = this.apiPayload?.settings?.embedded?.fill_container === true ||
            settings?.embedded?.fill_container === true;
        const hostCss = embedded
            ? fillHost
                ? `
      :host {
        display: block;
        position: relative;
        width: 100%;
        min-height: 100%;
        height: 100%;
        box-sizing: border-box;
      }
`
                : `
      :host {
        display: block;
        position: relative;
        width: 100%;
        min-height: 0;
        height: auto;
        box-sizing: border-box;
      }
`
            : `
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
`;
        const criticalStyles = document.createElement('style');
        criticalStyles.setAttribute('data-ms-stylesheet', 'critical');
        criticalStyles.textContent = `${hostCss}
      #adpx_popup,
      #adpx_modal-overlay,
      #adpx_popup * {
        pointer-events: auto;
      }
      #adpx_modal-overlay {
        pointer-events: auto;
      }

      /* COU popup mode: publisher template is the whole card — beat SOU.css min-width / landscape !important */
      #adpx_popup[data-ms-offer-chrome='template'] {
        min-width: unset !important;
        width: max-content !important;
        max-width: min(100vw - 24px, 560px) !important;
        min-height: unset !important;
        height: auto !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
        overflow: visible !important;
        background: transparent !important;
        background-color: transparent !important;
        box-shadow: none !important;
        border: none !important;
        padding: 0 !important;
        border-radius: 0 !important;
      }
      @media only screen and (max-device-width: 670px) and (orientation: landscape) {
        #adpx_popup[data-ms-offer-chrome='template'] {
          min-height: unset !important;
          height: auto !important;
        }
      }
      @media only screen and (min-device-width: 480px) and (max-device-width: 690px) and (orientation: landscape) {
        #adpx_popup[data-ms-offer-chrome='template'] {
          height: auto !important;
        }
      }
    `;
        this.shadowRoot.appendChild(criticalStyles);
        const souStyleElement = document.createElement('style');
        souStyleElement.setAttribute('data-ms-stylesheet', 'SOU');
        souStyleElement.textContent = getSOUStylesheet();
        this.shadowRoot.appendChild(souStyleElement);
        if (settings?.multi_offer_unit) {
            this.injectCSSFile(mouShadowStyles, 'MOU');
        }
        if (settings?.embedded?.enabled) {
            this.injectCSSFile(`${this.cdnUrl}/style.embed.css`, 'Embed');
        }
        /**
         * Progress-region defaults, immediately ahead of the publisher's COU CSS so
         * source order alone would already let theirs win. Every selector is
         * `:where()`-wrapped on top of that, so a publisher rule wins on specificity
         * too — no `!important` anywhere, and nothing to learn about overriding.
         */
        const progressDefaults = document.createElement('style');
        progressDefaults.setAttribute('data-ms-stylesheet', 'cou-progress-defaults');
        progressDefaults.textContent = COU_PROGRESS_DEFAULT_CSS;
        this.shadowRoot.appendChild(progressDefaults);
        const resolvedCou = resolveBundledCustomLayout(this.customLayout, settings);
        const customCss = resolvedCou?.stylesheet?.trim();
        if (customCss) {
            const couStyle = document.createElement('style');
            couStyle.setAttribute('data-ms-stylesheet', 'custom-layout');
            couStyle.textContent = customCss;
            this.shadowRoot.appendChild(couStyle);
        }
        if (this.lastResponseCss) {
            const apiCouStyle = document.createElement('style');
            apiCouStyle.setAttribute('data-ms-stylesheet', 'custom-layout-api');
            apiCouStyle.textContent = this.lastResponseCss;
            this.shadowRoot.appendChild(apiCouStyle);
        }
    }
    isFetchableStylesheetUrl(candidate) {
        const t = candidate.trim();
        if (t.startsWith('data:text/css'))
            return true;
        if (/^https?:\/\//i.test(t))
            return true;
        if (/^\/\//.test(t))
            return true;
        if (/^\.{0,2}\//.test(t) && /\.css(\?|$)/i.test(t))
            return true;
        if (t.startsWith('/') && /\.css(\?|$)/i.test(t))
            return true;
        return false;
    }
    injectCSSFile(cssUrlOrInline, name) {
        if (!this.shadowRoot)
            return;
        if (!this.isFetchableStylesheetUrl(cssUrlOrInline)) {
            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-ms-stylesheet', name);
            styleElement.textContent = cssUrlOrInline;
            this.shadowRoot.appendChild(styleElement);
            return;
        }
        const cssUrl = cssUrlOrInline;
        fetch(cssUrl)
            .then(response => {
            if (!response.ok)
                throw new Error(`Failed to fetch ${cssUrl}`);
            return response.text();
        })
            .then(cssText => {
            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-ms-stylesheet', name);
            styleElement.textContent = cssText;
            this.shadowRoot?.appendChild(styleElement);
        })
            .catch(error => {
            this.debug.warn(`Failed to fetch ${cssUrl}, trying fallbacks:`, error);
            const localPath = cssUrl.includes('style.css')
                ? '/SOU.css'
                : cssUrl.includes('style.mou.css')
                    ? '/MOU.css'
                    : cssUrl.includes('style.embed.css')
                        ? '/style.embed.css'
                        : null;
            if (localPath) {
                fetch(localPath)
                    .then(response => {
                    if (!response.ok)
                        throw new Error(`Failed to fetch ${localPath}`);
                    return response.text();
                })
                    .then(cssText => {
                    const styleElement = document.createElement('style');
                    styleElement.setAttribute('data-ms-stylesheet', name);
                    styleElement.textContent = cssText;
                    this.shadowRoot?.appendChild(styleElement);
                })
                    .catch(() => {
                    const linkElement = document.createElement('link');
                    linkElement.rel = 'stylesheet';
                    linkElement.href = cssUrl;
                    linkElement.setAttribute('data-ms-stylesheet', name);
                    this.shadowRoot?.appendChild(linkElement);
                });
            }
            else {
                const linkElement = document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = cssUrl;
                linkElement.setAttribute('data-ms-stylesheet', name);
                this.shadowRoot?.appendChild(linkElement);
            }
        });
    }
    /**
     * Tear down this instance — unmounts Preact tree, removes shadow host.
     * Safe to call multiple times.
     */
    destroy() {
        this.embedTargetObserver?.disconnect();
        this.embedTargetObserver = null;
        try {
            if (this.shadowRoot)
                render(null, this.shadowRoot);
        }
        catch (e) {
            this.debug.warn('destroy render error:', e);
        }
        if (this.shadowContainer?.parentNode) {
            this.shadowContainer.parentNode.removeChild(this.shadowContainer);
        }
        this.shadowContainer = null;
        this.shadowRoot = null;
        this.store.setShadowRoot(null);
        this.isInitialized = false;
    }
    renderApp() {
        const shadowRoot = this.getShadowRoot();
        if (!shadowRoot)
            return;
        const appProps = this.appApiKey ? { apiKey: this.appApiKey } : {};
        render(h(SDKContext.Provider, { value: { sdk: this, store: this.store } }, h(App, appProps)), shadowRoot);
        this.sendMessage(PLATFORM_MESSAGES.ADS_FOUND.name, {
            ...PLATFORM_MESSAGES.ADS_FOUND,
            total: this.store.getTotalOffers(),
        });
    }
    sendMessage(eventName, eventData) {
        const payload = {
            name: eventName,
            instance: this.instanceName,
            ...eventData,
        };
        const message = JSON.stringify(payload);
        try {
            window.parent?.postMessage(message, '*');
        }
        catch (e) {
            this.debug.warn('Error posting to parent', e);
        }
        try {
            window.ReactNativeWebView?.postMessage(message);
        }
        catch (e) {
            this.debug.warn('Error posting to ReactNativeWebView', e);
        }
        try {
            window.webkit?.messageHandlers?.adspostx?.postMessage(payload);
        }
        catch (e) {
            this.debug.warn('Error posting to webkit.messageHandlers', e);
        }
        if (typeof this.userCallback === 'function') {
            try {
                this.userCallback(eventName, payload);
            }
            catch (e) {
                this.debug.error('Error in userCallback', e);
            }
        }
    }
    _resetState() {
        this.currentAdIndex = 0;
    }
    async refresh() {
        if (!this.isInitialized) {
            this.debug.warn('Cannot refresh, SDK not initialized.');
            return;
        }
        this._resetState();
        await this.closeModal();
        await this.fetchAds();
    }
    async closeModal() {
        await this.store.closeModal();
    }
    onLinkClicked(event) {
        if (!this.isEmbedded)
            return true;
        event.preventDefault();
        event.stopPropagation();
        let targetUrl = '';
        if (event.target instanceof HTMLAnchorElement) {
            targetUrl = event.target.href;
        }
        this.sendMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
            ...PLATFORM_MESSAGES.URL_CLICKED,
            target_url: targetUrl,
        });
        return false;
    }
    getPayload() {
        return this.apiPayload ?? {};
    }
    setPayload(payloadUpdates) {
        if (this.apiPayload.accountId)
            delete payloadUpdates.accountId;
        if (this.apiPayload.awinAdvertiserId) {
            delete payloadUpdates.awinAdvertiserId;
        }
        if (this.apiPayload.adpx_fp)
            delete payloadUpdates.adpx_fp;
        this.apiPayload = { ...this.apiPayload, ...payloadUpdates };
        return this.apiPayload;
    }
    async reload() {
        if (!this.isInitialized) {
            this.debug.warn('Cannot reload, SDK not initialized.');
            return;
        }
        if (!this.adsOnDisplay || this.adsOnDisplay.length === 0) {
            this.debug.warn('Nothing to reload.');
            return;
        }
        this._resetState();
        await this.closeModal();
        this.store.emit('offers-updated');
        this.show();
    }
    // --- USP (User Selected Perks) per-instance API ---
    /** Per-account localStorage key so USP state is isolated per publisher. */
    getUspCacheKey() {
        const accountKey = this.accountId || this.awinAdvertiserId || 'default';
        const userKey = encodeURIComponent(String(this.apiPayload?.pub_user_id ?? 'anonymous'));
        return `${USP_SELECTED_OFFERS_KEY}__${accountKey}__${userKey}`;
    }
    /** Base URL for USP API endpoints, derived from the configured apiUrl. */
    getUspApiBase() {
        try {
            return new URL(this.apiUrl).origin + '/sdk/v4/usp';
        }
        catch {
            return new URL(DEFAULT_API_URL).origin + '/sdk/v4/usp';
        }
    }
    /** Add an offer ID to the per-instance USP selection and debounce a select API call. */
    addSelectedOffer(id) {
        if (!this.mouState.selectedOffers.includes(id)) {
            this.mouState.selectedOffers = [...this.mouState.selectedOffers, id];
        }
        const pendingUnselect = this._uspUnselectTimers.get(id);
        if (pendingUnselect != null) {
            clearTimeout(pendingUnselect);
            this._uspUnselectTimers.delete(id);
        }
        const pending = this._uspSelectTimers.get(id);
        if (pending != null)
            clearTimeout(pending);
        this._uspSelectTimers.set(id, setTimeout(() => {
            this._uspSelectTimers.delete(id);
            void this._fireUspSelect(id);
        }, 1000));
    }
    /** Remove an offer ID from the per-instance USP selection and debounce an unselect API call. */
    removeSelectedOffer(id) {
        this.mouState.selectedOffers = this.mouState.selectedOffers.filter(existing => existing !== id);
        const pendingSelect = this._uspSelectTimers.get(id);
        if (pendingSelect != null) {
            clearTimeout(pendingSelect);
            this._uspSelectTimers.delete(id);
        }
        const pending = this._uspUnselectTimers.get(id);
        if (pending != null)
            clearTimeout(pending);
        this._uspUnselectTimers.set(id, setTimeout(() => {
            this._uspUnselectTimers.delete(id);
            void this._fireUspUnselect(id);
        }, 1000));
    }
    get _uspAuthHeader() {
        const key = this.accountId || this.awinAdvertiserId;
        return key ? { 'Authorization': `Bearer ${key}` } : {};
    }
    async _fireUspSelect(id) {
        const sessionId = this.currentSessionId;
        const pubUserId = this.apiPayload?.pub_user_id;
        if (!sessionId || !pubUserId)
            return;
        try {
            await fetch(`${this.getUspApiBase()}/${sessionId}/${id}/select.json`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...this._uspAuthHeader },
                body: JSON.stringify({ pub_user_id: String(pubUserId) }),
            });
        }
        catch (e) {
            this.debug.warn(`USP select failed for offer ${id}`, e);
        }
    }
    async _fireUspUnselect(id) {
        const sessionId = this.currentSessionId;
        const pubUserId = this.apiPayload?.pub_user_id;
        if (!sessionId || !pubUserId)
            return;
        try {
            await fetch(`${this.getUspApiBase()}/${sessionId}/${id}/unselect.json`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...this._uspAuthHeader },
                body: JSON.stringify({ pub_user_id: String(pubUserId) }),
            });
        }
        catch (e) {
            this.debug.warn(`USP unselect failed for offer ${id}`, e);
        }
    }
    /** Fire the usp_event postMessage and PUT selected offers to the session endpoint. */
    dispatchUSPEvent() {
        this.sendMessage(PLATFORM_MESSAGES.USP_EVENT.name, {
            ...PLATFORM_MESSAGES.USP_EVENT,
            session_id: this.currentSessionId,
            selected_offers: [...this.mouState.selectedOffers],
        });
        const sessionId = this.currentSessionId;
        const pubUserId = this.apiPayload?.pub_user_id;
        const accountId = this.accountId || this.awinAdvertiserId;
        if (!sessionId || !pubUserId)
            return;
        fetch(`${this.getUspApiBase()}/session/${sessionId}.json?accountId=${accountId}&pub_user_id=${encodeURIComponent(String(pubUserId))}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                selected: [...this.mouState.selectedOffers],
                unselected: [],
            }),
        }).catch(e => this.debug.warn('USP event dispatch failed', e));
    }
    /** Complete the USP session: fire session_complete message, persist to localStorage, and POST to API. */
    completeSession() {
        this._uspSelectTimers.forEach(t => clearTimeout(t));
        this._uspSelectTimers.clear();
        this._uspUnselectTimers.forEach(t => clearTimeout(t));
        this._uspUnselectTimers.clear();
        const selectedOffers = [...this.mouState.selectedOffers];
        this.sendMessage(PLATFORM_MESSAGES.SESSION_COMPLETE.name, {
            ...PLATFORM_MESSAGES.SESSION_COMPLETE,
            session_id: this.currentSessionId,
            selected_offers: selectedOffers,
        });
        try {
            localStorage.setItem(this.getUspCacheKey(), JSON.stringify({ selectedOffers, timestamp: Date.now() }));
        }
        catch (e) {
            this.debug.warn('Could not persist USP session.', e);
        }
        const sessionId = this.currentSessionId;
        const pubUserId = this.apiPayload?.pub_user_id;
        if (!sessionId || !pubUserId)
            return;
        fetch(`${this.getUspApiBase()}/session/${sessionId}.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this._uspAuthHeader },
            body: JSON.stringify({ pub_user_id: String(pubUserId) }),
        }).catch(e => this.debug.warn('USP completeSession failed', e));
    }
    /**
     * Attempt to restore USP selections from localStorage (called internally
     * when a prefetch cache hit is detected). Returns true when selections were
     * restored so callers can set `reused_session`.
     */
    restoreUspSession() {
        try {
            const raw = localStorage.getItem(this.getUspCacheKey());
            if (!raw)
                return false;
            const parsed = JSON.parse(raw);
            const ageMs = Date.now() - (parsed.timestamp ?? 0);
            if (ageMs > 1 * 60 * 60 * 1000) {
                localStorage.removeItem(this.getUspCacheKey());
                return false;
            }
            if (Array.isArray(parsed.selectedOffers)) {
                this.mouState = { selectedOffers: [...parsed.selectedOffers] };
                return true;
            }
        }
        catch (e) {
            this.debug.warn('Could not restore USP session.', e);
        }
        return false;
    }
}
MomentScienceSDK.DEFAULT_PUBLISHER_SETTINGS = {
    ad_position: 'center',
    button_order: 'pos_first',
    close_delay: 0,
    darken_bg: true,
    darken_bg_non_centered: false,
    delay: 0,
    enable_close_delay: false,
    enable_effect_shimmer_pos_cta: false,
    enable_offerwall: false,
    enable_vertical_offset: false,
    fixed_progress_bar: true,
    hide_progress_bar_mobile: false,
    font_family: 'Roboto',
    mobile_vertical_offset: null,
    enable_pwaas_webhook: false,
    multi_offer_unit: false,
    offerwall_cta: 'More exclusive offers!',
    enable_perkswallet: false,
    perkswallet_show_cta: true,
    perkswallet_cta: 'Save to Perkswallet',
    saved_offer_text: 'Saved to Perkswallet',
    offerwall_show_cta: true,
    open_offerwall: false,
    show_disclaimer: false,
    open_overlay_offerwall: false,
    privacy: 'https://www.adspostx.com/end-user-privacy-policy',
    screen_margin: 0,
    show_ads_on_exit: false,
    close_ad_on_click_outside: false,
    show_close: true,
    embedded: {
        enabled: false,
        showHeader: true,
        showFooter: true,
        showBorder: true,
        targetElement: 'adpx',
        fill_container: false,
    },
    progress_bar_type: 'number',
};
